// Sized photos from the image service (imgproxy on the Kamal box, behind the CDN's /media/*): what each preset is
// and why the URLs look like this, docs/notes/app/image-service.md.

declare global {
  interface Window {
    __IMG_URL?: string
  }
}

/** The image service's base (`https://cdn.danchiano.dev/media`), or '' where none runs: dev, e2e, or before it is live. */
export function getRuntimeImgUrl(): string {
  if (typeof window !== 'undefined') return window.__IMG_URL ?? ''
  if (import.meta.env.DEV) return ''
  if (typeof process !== 'undefined' && process.env.E2E_PGLITE !== 'true') {
    return (process.env.URL_IMG ?? '').replace(/\/$/, '')
  }
  return ''
}

/** The square edges imgproxy has a preset for (IMGPROXY_PRESETS in infra/kamal/deploy.yml), smallest first. */
export const IMAGE_PRESET_EDGES = [64, 96, 128] as const

/** `face` fills the square (a person's photo), `logo` fits inside it whole. */
export type ImageFit = 'face' | 'logo'

const PRINTABLE_ASCII = /^[\x20-\x7e]+$/

const originOf = (base: string): string => base.replace(/^(https?:\/\/[^/]+).*$/, '$1')

const base64Url = (value: string): string => btoa(value).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '')

/**
 * `url` resized to the smallest preset at least twice `cssPx`, so a 2x screen draws it sharp. Only an image on the
 * service's own origin is sized (imgproxy refuses any other source); everything else comes back as it was.
 */
export function sizedImageUrl(base: string, url: string, cssPx: number, fit: ImageFit): string {
  if (!base || !url.startsWith(`${originOf(base)}/`) || !PRINTABLE_ASCII.test(url)) return url
  const edge = IMAGE_PRESET_EDGES.find((preset) => preset >= cssPx * 2) ?? 128
  return `${base}/_/${fit}-${edge}/${base64Url(url)}`
}
